import { shouldShowWhatsNew } from '@griply/utils';
import { useUpdateUserSettings } from 'features/user';
import { WHATS_NEW_CONTENT } from 'features/whats-new';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { version } from 'shared/constants';
import { WhatsNewDialog } from 'shared/dialogs/whats-new';
import { useUser } from 'shared/hooks/use-user';

import { WhatsNewContext, WhatsNewContextProps } from './whats-new-context';

const alwaysShow = import.meta.env.VITE_WHATS_NEW_ALWAYS_SHOW === 'true';

export type WhatsNewProviderProps = {
  children: React.ReactNode;
};

export const WhatsNewProvider: React.FC<WhatsNewProviderProps> = ({
  children,
}) => {
  const user = useUser();
  const updateSettings = useUpdateUserSettings();
  const hasEvaluated = useRef(false);
  const [open, setOpen] = useState(false);

  const openWhatsNew = useCallback(() => setOpen(true), []);
  const closeWhatsNew = useCallback(() => setOpen(false), []);

  const value = useMemo<WhatsNewContextProps>(
    () => ({ openWhatsNew }),
    [openWhatsNew],
  );

  useEffect(() => {
    if (hasEvaluated.current || !user) {
      return;
    }
    hasEvaluated.current = true;

    const show =
      alwaysShow ||
      shouldShowWhatsNew({
        contentVersion: WHATS_NEW_CONTENT?.version ?? null,
        currentVersion: version,
        lastLaunchedVersion: null,
        lastSeenWhatsNewVersion:
          user.settings?.lastSeenWhatsNewVersion?.desktop ?? null,
        accountCreatedAtMs: user.createdAt.getTime(),
        nowMs: Date.now(),
      });

    if (!show || !WHATS_NEW_CONTENT) {
      return;
    }

    setOpen(true);
    updateSettings({
      lastSeenWhatsNewVersion: { desktop: WHATS_NEW_CONTENT.version },
    });
  }, [user, updateSettings]);

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      {!!user && !!WHATS_NEW_CONTENT && (
        <WhatsNewDialog
          open={open}
          onClose={closeWhatsNew}
          pages={WHATS_NEW_CONTENT.pages}
        />
      )}
    </WhatsNewContext.Provider>
  );
};
